import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, Ticket, Loader2, User, MessageCircle, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

// FAQ data pour le chatbot - Informations à jour
const FAQ_DATA = [
  {
    question: "Comment fonctionne Middleman?",
    answer: "Middleman est un intermédiaire de confiance qui sécurise les transferts de billets entre particuliers.\n\n1️⃣ Créez une demande de transfert\n2️⃣ Les deux parties confirment les détails\n3️⃣ L'acheteur paie par virement Interac\n4️⃣ Le vendeur transfère les billets à Middleman\n5️⃣ Nous vérifions et transférons les billets\n6️⃣ Le vendeur reçoit son paiement\n\nProtection des deux parties garantie!",
    keywords: ["fonctionne", "comment", "marche", "utiliser", "service", "étapes", "processus"]
  },
  {
    question: "Quels sont les frais?",
    answer: "Nos frais sont simples et transparents, basés sur le montant total du transfert :\n\n💰 6,95$ pour les transferts de 125$ et moins\n💰 9,95$ pour les transferts de 125,01$ à 250$\n💰 19,95$ pour les transferts de 250,01$ et plus\n\nVous ne payez qu'une seule fois les frais par transfert, peu importe le nombre de billets.\n\nExemples:\n• Transfert de 100$ → Frais de 6,95$\n• Transfert de 200$ → Frais de 9,95$\n• Transfert de 500$ → Frais de 19,95$",
    keywords: ["frais", "coût", "prix", "tarif", "combien", "payer", "6.95", "9.95", "19.95", "fixe", "total"]
  },
  {
    question: "Comment sont vérifiés les billets?",
    answer: "Les billets sont transférés dans notre compte de vérification officiel. Nous vérifions :\n\n✅ Authenticité des billets\n✅ Correspondance avec l'événement\n✅ Numéros de section/rangée/siège\n✅ Date et heure de l'événement\n\nAucun billet n'est transféré à l'acheteur avant validation complète.",
    keywords: ["vérification", "vérifié", "authentique", "faux", "fraude", "sécurité", "ticketmaster"]
  },
  {
    question: "Mon argent est-il en sécurité?",
    answer: "Oui, absolument! 🔒\n\nVos fonds sont sécurisés par Middleman jusqu'à ce que :\n• Les billets soient vérifiés ✓\n• Les billets soient transférés à l'acheteur ✓\n\nNous ne libérons les fonds au vendeur qu'après ces deux étapes. Protection des deux parties!",
    keywords: ["argent", "sécurité", "sécurisé", "protégé", "fonds", "paiement"]
  },
  {
    question: "Que se passe-t-il si les billets sont faux?",
    answer: "Si nos vérifications révèlent un problème :\n\n❌ Le transfert est immédiatement annulé\n💰 L'acheteur est remboursé à 100%\n⚠️ Des mesures sont prises contre le vendeur\n\nVous êtes protégé contre la fraude!",
    keywords: ["faux", "invalide", "problème", "remboursement", "annulation", "fraude"]
  },
  {
    question: "Quels sont les types d'arnaques fréquents?",
    answer: "Nous avons analysé des centaines de cas de fraudes entre 2010 et 2026. Les plus courants :\n\n❌ Billet jamais livré - Le vendeur disparaît après paiement\n❌ Faux screenshots - Capture d'écran truquée d'un transfert\n❌ Faux billets PDF - Découverts à l'entrée de l'événement\n❌ Comptes piratés - Fraude via compte Facebook/Instagram piraté\n\n85% de ces fraudes se produisent sur les réseaux sociaux sans protection!",
    keywords: ["arnaque", "fraude", "escroquerie", "faux", "piégé", "volé", "perdu"]
  },
  {
    question: "Comment Middleman me protège des fraudes?",
    answer: "Middleman a été conçu pour bloquer ces fraudes :\n\n✅ Les billets d'abord, l'argent ensuite\n✅ Les screenshots ne comptent pas - seuls les vrais billets transférés\n✅ Aucun échange de coordonnées personnelles\n✅ Chaque étape visible dans une timeline\n✅ Intermédiaire neutre qui protège les deux parties\n\nNotre principe : validation avant paiement!",
    keywords: ["protection", "sécurité", "fraude", "arnaque", "protège", "sûr"]
  },
  {
    question: "Quels modes de paiement acceptez-vous?",
    answer: "Nous acceptons le Virement Interac 🏦\n\n• Disponible avec toutes les banques canadiennes\n• Rapide et sécurisé\n• Aucun frais bancaire supplémentaire\n\nLes fonds sont sécurisés par Middleman jusqu'à la fin du transfert.",
    keywords: ["paiement", "payer", "interac", "virement", "banque", "carte"]
  },
  {
    question: "Combien de temps prend un transfert?",
    answer: "⏱️ Délai typique : 24 à 48 heures\n\nCela dépend de :\n• La rapidité du paiement par l'acheteur\n• La rapidité du transfert de billets par le vendeur\n• La vérification (15 min à 2h)\n\nVous recevez des notifications à chaque étape!",
    keywords: ["temps", "durée", "délai", "rapide", "combien", "heures", "jours"]
  },
  {
    question: "Puis-je annuler un transfert?",
    answer: "📋 Avant le paiement : Annulation libre\n\n📋 Après le paiement : L'annulation nécessite l'accord des deux parties et des frais peuvent s'appliquer.\n\nContactez-nous rapidement si vous avez besoin d'annuler!",
    keywords: ["annuler", "annulation", "rembourser", "retour", "changer"]
  },
  {
    question: "Qui paie les frais?",
    answer: "C'est flexible! Lors de la création du transfert, vous choisissez :\n\n• 👤 L'acheteur paie les frais\n• 👤 Le vendeur paie les frais\n• 🤝 Partagé 50/50\n\nÀ vous de décider!",
    keywords: ["qui paie", "frais", "acheteur", "vendeur", "partage", "répartition"]
  },
  {
    question: "Où se produisent les fraudes de billets?",
    answer: "La majorité des fraudes proviennent de :\n\n📱 Groupes Facebook de revente\n📱 Facebook Marketplace\n📱 Instagram\n📱 Autres plateformes sociales\n\nPoint commun : aucune validation des billets, aucun mécanisme de protection, paiement direct entre inconnus = zéro filet de sécurité!",
    keywords: ["facebook", "marketplace", "instagram", "réseaux", "social", "groupe"]
  },
  {
    question: "Comment contacter le support?",
    answer: "Plusieurs options pour nous joindre :\n\n💬 Ce chatbot - Réponse immédiate\n📱 Messenger - m.me/466226189896489 - Réponse < 1h\n📧 info@transfertdebillets.ca - Réponse < 24h\n\nVous pouvez aussi créer un ticket de support directement ici!",
    keywords: ["contact", "support", "aide", "joindre", "assistance", "email", "messenger"]
  },
  {
    question: "Quels types de billets acceptez-vous?",
    answer: "Nous acceptons les billets électroniques transférables :\n\n✅ Ticketmaster\n✅ Evenko\n✅ Autres plateformes officielles\n\nLe vendeur doit pouvoir transférer les billets à notre compte de vérification.",
    keywords: ["billets", "type", "ticketmaster", "evenko", "électronique", "accepté"]
  }
];

const FAQChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Bonjour! 👋 Je suis l'assistant Middleman. Comment puis-je vous aider?\n\n📧 info@transfertdebillets.ca",
      suggestions: ["Comment fonctionne Middleman?", "Quels sont les frais?", "Mon argent est-il en sécurité?"]
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    customer_name: '',
    customer_email: '',
    description: ''
  });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [noAnswerCount, setNoAnswerCount] = useState(0);
  const [askedSatisfaction, setAskedSatisfaction] = useState(false);
  const [waitingForHumanAgent, setWaitingForHumanAgent] = useState(false);
  const [humanAgentTimer, setHumanAgentTimer] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [pendingSupportType, setPendingSupportType] = useState(null);
  const messagesEndRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Check if within business hours (9:00 - 21:00 EST)
  const isWithinBusinessHours = () => {
    const now = new Date();
    // Convert to EST
    const estTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Toronto" }));
    const hour = estTime.getHours();
    return hour >= 9 && hour < 21;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, showTicketForm]);

  // Cleanup timer on unmount or close
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Start the timer when human agent is requested
  const startHumanAgentTimer = () => {
    setHumanAgentTimer(new Date());
    setTimerSeconds(0);
    
    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    // Start new interval
    timerIntervalRef.current = setInterval(() => {
      setTimerSeconds(prev => prev + 1);
    }, 1000);
  };

  // Format timer display
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const findBestAnswer = (query) => {
    const queryLower = query.toLowerCase();
    let bestMatch = null;
    let bestScore = 0;

    for (const faq of FAQ_DATA) {
      let score = 0;
      
      // Check keywords
      for (const keyword of faq.keywords) {
        if (queryLower.includes(keyword.toLowerCase())) {
          score += 2;
        }
      }
      
      // Check question similarity
      const questionWords = faq.question.toLowerCase().split(' ');
      for (const word of questionWords) {
        if (word.length > 3 && queryLower.includes(word)) {
          score += 1;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = faq;
      }
    }

    return bestScore >= 2 ? bestMatch : null;
  };

  const handleSend = (text = inputValue) => {
    if (!text.trim()) return;

    // Add user message
    setMessages(prev => [...prev, { type: 'user', text }]);
    setInputValue('');

    // Handle satisfaction response
    if (askedSatisfaction) {
      const lowerText = text.toLowerCase();
      if (lowerText.includes('oui') || lowerText.includes('yes') || lowerText.includes('merci')) {
        setAskedSatisfaction(false);
        setMessages(prev => [...prev, {
          type: 'bot',
          text: "Parfait! 😊 Je suis content d'avoir pu vous aider. N'hésitez pas si vous avez d'autres questions!",
          suggestions: ["Comment fonctionne Middleman?", "Quels sont les frais?", "Comment contacter le support?"]
        }]);
        return;
      } else if (lowerText.includes('non') || lowerText.includes('no') || lowerText.includes('pas')) {
        setAskedSatisfaction(false);
        handleNeedHumanAgent();
        return;
      }
    }

    // Find answer
    setTimeout(() => {
      const answer = findBestAnswer(text);
      
      if (answer) {
        setNoAnswerCount(0);
        setMessages(prev => [...prev, {
          type: 'bot',
          text: answer.answer,
          askSatisfaction: true
        }]);
        // Ask satisfaction after a delay
        setTimeout(() => {
          setAskedSatisfaction(true);
          setMessages(prev => [...prev, {
            type: 'bot',
            text: "Cette réponse a-t-elle répondu à votre question?",
            satisfactionButtons: true
          }]);
        }, 1500);
      } else {
        const newCount = noAnswerCount + 1;
        setNoAnswerCount(newCount);
        
        if (newCount >= 2) {
          handleNeedHumanAgent();
        } else {
          setMessages(prev => [...prev, {
            type: 'bot',
            text: "Je n'ai pas trouvé de réponse exacte. Voici des sujets populaires :",
            suggestions: ["Comment fonctionne Middleman?", "Quels sont les frais?", "Comment contacter le support?"]
          }]);
        }
      }
    }, 500);
  };

  const handleNeedHumanAgent = () => {
    if (isWithinBusinessHours()) {
      // Within business hours - ask what type of help they need
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Je comprends que vous avez besoin d'aide supplémentaire. 🤝\n\nPour mieux vous diriger, quel type d'assistance vous convient le mieux?",
        supportTypeButtons: true
      }]);
    } else {
      // Outside business hours - offer support form
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Je comprends que vous avez besoin d'aide supplémentaire. 🤝\n\nNos agents sont disponibles de 9h à 21h (heure de l'Est). En dehors de ces heures, vous pouvez nous laisser un message via le formulaire de support et nous vous répondrons dès que possible.",
        showTicketButton: true
      }]);
    }
  };

  const handleSupportTypeSelection = (supportType) => {
    const typeLabels = {
      customer_service: "Service à la clientèle",
      technical_support: "Support technique"
    };
    
    setMessages(prev => [...prev, {
      type: 'bot',
      text: `Parfait! Un agent du ${typeLabels[supportType]} va être notifié de votre demande.\n\nSouhaitez-vous être mis en communication maintenant?`,
      humanAgentButtons: true,
      selectedSupportType: supportType
    }]);
  };

  // Show contact form before requesting human agent
  const handleShowContactForm = (supportType = 'customer_service') => {
    // Find the support type from the last message that had it
    const lastMessageWithType = [...messages].reverse().find(m => m.selectedSupportType);
    const finalSupportType = lastMessageWithType?.selectedSupportType || supportType;
    
    setPendingSupportType(finalSupportType);
    setShowContactForm(true);
    
    setMessages(prev => [...prev, {
      type: 'bot',
      text: "Pour que nous puissions vous contacter, veuillez remplir vos coordonnées ci-dessous. 👇"
    }]);
  };

  // Validate and submit contact form, then request human agent
  const handleSubmitContactForm = async () => {
    // Validate required fields
    if (!contactInfo.name.trim()) {
      toast.error("Veuillez entrer votre nom complet");
      return;
    }
    if (!contactInfo.email.trim() || !contactInfo.email.includes('@')) {
      toast.error("Veuillez entrer une adresse courriel valide");
      return;
    }
    
    setShowContactForm(false);
    await handleRequestHumanAgent(pendingSupportType);
  };

  const handleRequestHumanAgent = async (supportType = 'customer_service') => {
    setWaitingForHumanAgent(true);
    
    // Start the timer immediately when human assistance is requested
    startHumanAgentTimer();
    
    // Find the support type from the last message that had it
    const lastMessageWithType = [...messages].reverse().find(m => m.selectedSupportType);
    const finalSupportType = lastMessageWithType?.selectedSupportType || supportType;
    
    const typeLabels = {
      customer_service: "Service à la clientèle",
      technical_support: "Support technique"
    };
    
    // Build contact info string for ticket
    const contactDetails = [
      `Nom: ${contactInfo.name}`,
      `Courriel: ${contactInfo.email}`,
      contactInfo.phone ? `Téléphone: ${contactInfo.phone}` : null
    ].filter(Boolean).join('\n');
    
    // Build full chat history with both user messages AND bot responses
    const fullChatHistory = messages
      .filter(m => m.text) // Only messages with text content
      .map(m => {
        const prefix = m.type === 'user' ? '👤 Client' : '🤖 Bot';
        return `${prefix}: ${m.text}`;
      })
      .join('\n\n');
    
    // Create a support ticket marked as urgent/live
    try {
      // Create ticket with real customer contact info and full conversation
      await axios.post(`${API}/support/tickets`, {
        customer_name: contactInfo.name.trim(),
        customer_email: contactInfo.email.trim().toLowerCase(),
        customer_phone: contactInfo.phone.trim() || null,
        subject: `🔴 DEMANDE DE CHAT EN DIRECT - ${typeLabels[finalSupportType]}`,
        description: `Un client demande une assistance en direct.\n\n📋 COORDONNÉES DU CLIENT:\n${contactDetails}\n\nType de support: ${typeLabels[finalSupportType]}\n\n💬 HISTORIQUE COMPLET DE LA CONVERSATION:\n\n${fullChatHistory}`,
        category: finalSupportType === 'technical_support' ? 'support_technique_live' : 'service_clientele_live',
        priority: 'high',
        source: 'chatbot_live',
        support_type: finalSupportType
      });
      
      // Send dedicated SMS alert for live chat request with customer info
      try {
        await axios.post(`${API}/support/live-chat-alert`, {
          support_type: typeLabels[finalSupportType],
          chat_history_preview: fullChatHistory.slice(0, 100),
          customer_name: contactInfo.name.trim(),
          customer_phone: contactInfo.phone.trim() || null
        });
      } catch (smsError) {
        console.log("SMS alert sent via ticket creation");
      }
      
      setMessages(prev => [...prev, {
        type: 'bot',
        text: `✅ Un agent a été notifié de votre demande!\n\n👤 ${contactInfo.name}\n📧 ${contactInfo.email}${contactInfo.phone ? `\n📱 ${contactInfo.phone}` : ''}\n\n⏱️ Chronomètre démarré - nous mesurons notre temps de réponse.\n\nNous vous contacterons à ces coordonnées très bientôt!`,
        messengerButton: true,
        showTimer: true
      }]);
      
      toast.success("Un agent a été notifié par SMS!");
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Désolé, une erreur s'est produite. Veuillez nous contacter:\n\n📧 info@transfertdebillets.ca\n\nOu via le formulaire de support.",
        showTicketButton: true
      }]);
    }
    
    setWaitingForHumanAgent(false);
  };

  const handleSatisfactionResponse = (satisfied) => {
    setAskedSatisfaction(false);
    if (satisfied) {
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Parfait! 😊 Je suis content d'avoir pu vous aider. N'hésitez pas si vous avez d'autres questions!",
        suggestions: ["Comment fonctionne Middleman?", "Quels sont les frais?", "Comment contacter le support?"]
      }]);
    } else {
      handleNeedHumanAgent();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.customer_name || !ticketForm.customer_email) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSubmittingTicket(true);
    try {
      // Build description from chat history
      const chatHistory = messages
        .filter(m => m.type === 'user')
        .map(m => m.text)
        .join('\n');
      
      const response = await axios.post(`${API}/support/tickets`, {
        customer_name: ticketForm.customer_name,
        customer_email: ticketForm.customer_email,
        subject: `Question via chatbot: ${messages.find(m => m.type === 'user')?.text?.slice(0, 50) || 'Demande de support'}`,
        description: ticketForm.description 
          ? `${ticketForm.description}\n\n--- Historique du chat ---\n${chatHistory}`
          : `Questions posées dans le chat:\n${chatHistory}`,
        category: 'autre',
        priority: '48h',
        source: 'chatbot'
      });

      setShowTicketForm(false);
      setTicketForm({ customer_name: '', customer_email: '', description: '' });
      setMessages(prev => [...prev, {
        type: 'bot',
        text: `✅ Ticket créé avec succès!\n\n📋 Référence: #${response.data.ticket_id?.slice(0, 8).toUpperCase()}\n\nNous vous répondrons à ${ticketForm.customer_email} rapidement!`,
        suggestions: ["Comment fonctionne Middleman?", "Quels sont les frais?"]
      }]);
      toast.success("Ticket créé avec succès!");
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error("Erreur lors de la création du ticket");
    }
    setSubmittingTicket(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-4 w-96 max-w-[calc(100vw-2rem)] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 animate-slideUp" data-testid="faq-chatbot">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-500 to-brand-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-semibold">Assistant Middleman</h3>
            <p className="text-xs text-brand-100">Réponse immédiate</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
          data-testid="chatbot-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div key={index}>
            <div
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] p-3 rounded-2xl whitespace-pre-wrap break-words overflow-hidden ${
                  message.type === 'user'
                    ? 'bg-brand-500 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              >
                {message.text}
              </div>
            </div>
            
            {/* Ticket Button */}
            {message.showTicketButton && message.type === 'bot' && (
              <div className="mt-3">
                <Button
                  onClick={() => setShowTicketForm(true)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="create-ticket-from-chat"
                >
                  <Ticket className="h-4 w-4 mr-2" />
                  Créer un ticket de support
                </Button>
              </div>
            )}
            
            {/* Satisfaction Buttons */}
            {message.satisfactionButtons && message.type === 'bot' && (
              <div className="mt-3 flex gap-2">
                <Button
                  onClick={() => handleSatisfactionResponse(true)}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  data-testid="satisfaction-yes"
                >
                  ✓ Oui, merci!
                </Button>
                <Button
                  onClick={() => handleSatisfactionResponse(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white"
                  data-testid="satisfaction-no"
                >
                  ✗ Non, j'ai besoin d'aide
                </Button>
              </div>
            )}
            
            {/* Support Type Selection Buttons */}
            {message.supportTypeButtons && message.type === 'bot' && (
              <div className="mt-3 flex flex-col gap-2">
                <Button
                  onClick={() => handleSupportTypeSelection('customer_service')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-left justify-start"
                  data-testid="support-type-customer"
                >
                  <User className="h-4 w-4 mr-2" />
                  <div className="text-left">
                    <div className="font-semibold">Service à la clientèle</div>
                    <div className="text-xs opacity-80">État de demande, paiement, billets, renseignements</div>
                  </div>
                </Button>
                <Button
                  onClick={() => handleSupportTypeSelection('technical_support')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white text-left justify-start"
                  data-testid="support-type-technical"
                >
                  <Bot className="h-4 w-4 mr-2" />
                  <div className="text-left">
                    <div className="font-semibold">Support technique</div>
                    <div className="text-xs opacity-80">Problème technique sur le site ou une fonction</div>
                  </div>
                </Button>
              </div>
            )}
            
            {/* Human Agent Buttons */}
            {message.humanAgentButtons && message.type === 'bot' && (
              <div className="mt-3 flex flex-col gap-2">
                <Button
                  onClick={() => handleShowContactForm()}
                  disabled={waitingForHumanAgent}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                  data-testid="request-human-agent"
                >
                  {waitingForHumanAgent ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 mr-2" />
                      Oui, parler à un agent
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setShowTicketForm(true)}
                  variant="outline"
                  className="w-full"
                >
                  <Ticket className="h-4 w-4 mr-2" />
                  Laisser un message plutôt
                </Button>
              </div>
            )}
            
            {/* Messenger Button */}
            {message.messengerButton && message.type === 'bot' && (
              <div className="mt-3 space-y-3">
                {/* Timer Display */}
                {message.showTimer && humanAgentTimer && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-amber-800 font-medium text-sm">En attente d'un agent</span>
                    </div>
                    <div className="font-mono text-lg font-bold text-amber-700" data-testid="human-agent-timer">
                      ⏱️ {formatTimer(timerSeconds)}
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => window.open('https://m.me/466226189896489', '_blank')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Contacter via Messenger
                </Button>
              </div>
            )}
            
            {/* Suggestions */}
            {message.suggestions && message.type === 'bot' && !message.showTicketButton && !message.satisfactionButtons && (
              <div className="mt-2 flex flex-wrap gap-2">
                {message.suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-xs px-3 py-1.5 bg-brand-50 text-brand-700 rounded-full hover:bg-brand-100 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        
        {/* Ticket Form */}
        {showTicketForm && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-orange-800 flex items-center gap-2">
              <Ticket className="h-4 w-4" />
              Créer un ticket
            </h4>
            <div>
              <Input
                placeholder="Votre nom *"
                value={ticketForm.customer_name}
                onChange={(e) => setTicketForm({ ...ticketForm, customer_name: e.target.value })}
                className="mb-2"
              />
              <Input
                type="email"
                placeholder="Votre email *"
                value={ticketForm.customer_email}
                onChange={(e) => setTicketForm({ ...ticketForm, customer_email: e.target.value })}
                className="mb-2"
              />
              <textarea
                placeholder="Détails supplémentaires (optionnel)"
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                className="w-full p-2 border rounded-md text-sm resize-none"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTicketForm(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleCreateTicket}
                disabled={submittingTicket}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                {submittingTicket ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Envoyer"
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Contact Form for Human Agent Request */}
        {showContactForm && (
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-brand-800 flex items-center gap-2">
              <User className="h-4 w-4" />
              Vos coordonnées
            </h4>
            <p className="text-xs text-brand-600">Pour que nous puissions vous contacter si vous quittez le chat</p>
            <div>
              <Input
                placeholder="Nom complet *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                className="mb-2"
                data-testid="contact-name-input"
              />
              <Input
                type="email"
                placeholder="Adresse courriel *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                className="mb-2"
                data-testid="contact-email-input"
              />
              <Input
                type="tel"
                placeholder="Numéro de téléphone (optionnel)"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                className="mb-2"
                data-testid="contact-phone-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowContactForm(false);
                  setPendingSupportType(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitContactForm}
                disabled={waitingForHumanAgent || !contactInfo.name.trim() || !contactInfo.email.includes('@')}
                className="flex-1 bg-brand-600 hover:bg-brand-700"
                data-testid="submit-contact-form"
              >
                {waitingForHumanAgent ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Parler à un agent"
                )}
              </Button>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        {/* Email always visible */}
        <div className="text-center mb-2 text-xs text-gray-500">
          📧 <a href="mailto:info@transfertdebillets.ca" className="text-brand-600 hover:underline">info@transfertdebillets.ca</a>
        </div>
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Posez votre question..."
            className="flex-1"
            data-testid="chatbot-input"
          />
          <Button
            onClick={() => handleSend()}
            className="bg-brand-500 hover:bg-brand-600"
            disabled={!inputValue.trim()}
            data-testid="chatbot-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Report bug link */}
        <div className="flex justify-center pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => {
              onClose?.();
              setTimeout(() => {
                const supportBtn = document.querySelector('[data-testid="floating-support-btn"]');
                if (supportBtn) supportBtn.click();
              }, 300);
            }}
            className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-brand-500 transition-colors"
          >
            <Bug className="h-3 w-3" />
            <span>Rapporter un problème</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default FAQChatbot;
